import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Button, Group, SimpleGrid, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  FilterDrawer,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type FilterField,
  type FilterValues,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { admin } from '@repletoryreach/sdk-api';
import { usePersistedState } from '../../../lib/usePersistedState';
import { useTenantMap } from '../../../data/useTenants';
import { formatInteger, formatMinor } from '../../../lib/format';
import { SubscriptionsTable, statusLabel } from './components/SubscriptionsTable';
import { SubscriptionsList } from './components/SubscriptionsList';
import { RenewalSettingsCard } from './components/RenewalSettingsCard';
import { ExtraUseSettingsCard } from './components/ExtraUseSettingsCard';
import { StatCard } from '../../../components/StatCard';
import { CurrencySwitch, amountIn, useDisplayCurrency } from '../../../components/CurrencySwitch';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { TableFooter } from '../../../components/TableFooter';
import { BillingToolbar } from '../components/BillingToolbar';
import { ViewToggle } from '../components/ViewToggle';
import { IconClock, IconLayers, IconRepeat, IconTrend, IconAdjustment } from '../components/icons';
import { useT } from '../../../i18n/I18nProvider';
import '../components/billing.css';

type KpiKey = 'all' | 'active' | 'past_due' | 'cancelled';

const KPI_KEYS: KpiKey[] = ['all', 'active', 'past_due', 'cancelled'];

const STAT_COLS = { base: 1, sm: 2, lg: 5 };

export default function SubscriptionsPage() {
  const t = useT();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const subsQ = useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () => admin.listSubscriptions()
  });
  const plansQ = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => admin.listPlans()
  });

  const all = useMemo(() => subsQ.data?.data ?? [], [subsQ.data]);
  const plans = useMemo(() => plansQ.data?.data ?? [], [plansQ.data]);
  const { map: tenants } = useTenantMap();
  const planById = useMemo(() => new Map(plans.map((p) => [p.id, p])), [plans]);
  const planLabel = useMemo(
    () => (planId: string) => planById.get(planId)?.code ?? t('billing.subscriptions.unknownPlan'),
    [planById, t]
  );
  const planPrice = useMemo(
    () => (planId: string) => {
      const plan = planById.get(planId);
      return plan ? { amount_minor: plan.monthly_amount_minor, currency: plan.currency } : undefined;
    },
    [planById]
  );

  const [view, setView] = usePersistedState<ViewMode>('admin.subscriptions.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.subscriptions.pageSize', 25);

  const [query, setQuery] = useState('');
  // The billing hub links here with `?status=past_due`, so the page opens on
  // the slice the operator clicked rather than on everything.
  const initialKpi = KPI_KEYS.find((k) => k === params.get('status')) ?? 'all';
  const [kpi, setKpi] = useState<KpiKey | null>(initialKpi);
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;

  function clearAllFilters() {
    setQuery('');
    setKpi('all');
    setFilters({});
    setPage(1);
  }

  /**
   * A subscription with a `cancelled_at` stamp is cancelled whatever its status
   * column says. The plans page already counted it that way and this one did
   * not, so the same dataset gave two answers in two places.
   */
  const isCancelled = (s: admin.AdminSubscription) => s.status === 'cancelled' || !!s.cancelled_at;

  const counts = useMemo(() => ({
    all: all.length,
    active: all.filter((s) => s.status === 'active').length,
    past_due: all.filter((s) => s.status === 'past_due').length,
    cancelled: all.filter(isCancelled).length
  }), [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all',       label: t('billing.subscriptions.kpi.all'),     value: counts.all,       tone: 'primary' },
    { id: 'active',    label: t('status.active'),                     value: counts.active,    tone: 'healthy' },
    { id: 'past_due',  label: t('billing.subscriptions.kpi.pastDue'), value: counts.past_due,  tone: 'warning' },
    { id: 'cancelled', label: t('status.cancelled'),                  value: counts.cancelled, tone: 'down' }
  ];

  /**
   * Monthly recurring revenue: each active subscription's plan price, kept
   * separate per currency because summing minor units across currencies would
   * produce a number that means nothing.
   */
  const mrrByCurrency = useMemo(() => {
    const byCurrency = new Map<string, number>();
    for (const s of all) {
      if (s.status !== 'active') continue;
      const plan = planById.get(s.plan_id);
      if (!plan) continue;
      byCurrency.set(plan.currency, (byCurrency.get(plan.currency) ?? 0) + plan.monthly_amount_minor);
    }
    return byCurrency;
  }, [all, planById]);
  // One currency on the card at a time, remembered across the billing pages.
  const mrrCurrencies = useMemo(() => Array.from(mrrByCurrency.entries()).sort(([, a], [, b]) => b - a).map(([c]) => c), [mrrByCurrency]);
  const [displayCurrency, setDisplayCurrency] = useDisplayCurrency(mrrCurrencies);
  const mrr = amountIn(mrrByCurrency, displayCurrency);
  const otherCurrencies = mrrCurrencies.filter((c) => c !== displayCurrency);

  /** Cycles that end before this month is out: what the renewal worker is about to charge. */
  const renewalsThisMonth = useMemo(() => {
    const now = new Date();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
    return all.filter((s) => {
      if (isCancelled(s)) return false;
      const end = new Date(s.billing_cycle_end).getTime();
      return !Number.isNaN(end) && end <= monthEnd;
    }).length;
  }, [all]);

  const filterFields: FilterField[] = useMemo(() => {
    const statuses = Array.from(new Set(all.map((s) => s.status))).sort();
    return [
      {
        key: 'status',
        label: t('common.status'),
        placeholder: t('billing.subscriptions.filters.anyStatus'),
        options: statuses.map((s) => ({ value: s, label: statusLabel(t, s) }))
      },
      {
        key: 'plan_id',
        label: t('billing.subscriptions.filters.plan'),
        placeholder: t('billing.subscriptions.filters.anyPlan'),
        options: plans.map((p) => ({ value: p.id, label: p.code }))
      }
    ];
  }, [all, plans, t]);

  const pills: ActiveFilterPill[] = [];
  if (kpi && kpi !== 'all') pills.push({ id: 'kpi', field: t('common.status'), value: statusLabel(t, kpi) });
  if (filters.status) pills.push({ id: 'status', field: t('common.status'), value: statusLabel(t, filters.status) });
  if (filters.plan_id) pills.push({ id: 'plan_id', field: t('billing.subscriptions.filters.plan'), value: planLabel(filters.plan_id) });
  function removePill(id: string) {
    setPage(1);
    if (id === 'kpi') { setKpi('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi === 'cancelled') rows = rows.filter(isCancelled);
    else if (kpi && kpi !== 'all') rows = rows.filter((s) => s.status === kpi);
    if (filters.status) rows = rows.filter((s) => s.status === filters.status);
    if (filters.plan_id) rows = rows.filter((s) => s.plan_id === filters.plan_id);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (s) =>
          (tenants.get(s.organisation_id)?.name ?? '').toLowerCase().includes(q) ||
          (tenants.get(s.organisation_id)?.slug ?? '').toLowerCase().includes(q) ||
          planLabel(s.plan_id).toLowerCase().includes(q) ||
          s.status.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [all, kpi, filters, query, planLabel, tenants]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const loadError = subsQ.error ?? plansQ.error;
  const loading = subsQ.isLoading || plansQ.isLoading;

  const rowNav = {
    onRowClick: (s: admin.AdminSubscription) => nav(`/tenants/${s.organisation_id}`),
    onOpenLedger: (s: admin.AdminSubscription) => nav(`/billing/ledger?org_id=${s.organisation_id}`),
    onOpenInvoices: (s: admin.AdminSubscription) => nav(`/billing/invoices?org_id=${s.organisation_id}`)
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.subscriptions.eyebrow')}
        title={t('billing.subscriptions.title')}
        description={t('billing.subscriptions.description')}
        actions={<ViewToggle value={view === 'cards' ? 'table' : view} onChange={setView} />}
      />

      {counts.past_due > 0 && (
        <Alert color="warning" title={t('billing.subscriptions.pastDue.title', { count: counts.past_due })}>
          {t('billing.subscriptions.pastDue.banner')}
        </Alert>
      )}

      <CurrencySwitch currencies={mrrCurrencies} value={displayCurrency} onChange={setDisplayCurrency} />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard
          tone="success" icon={<IconTrend />}
          label={t('billing.subscriptions.metric.mrr')}
          value={mrr || t('billing.subscriptions.metric.noRevenue')}
          hint={otherCurrencies.length > 0
            ? t('billing.currency.othersHint', { list: otherCurrencies.join(', ') })
            : t('billing.subscriptions.metric.mrrHint', { count: formatInteger(counts.active) })}
          loading={loading}
        />
        <StatCard
          tone="primary" icon={<IconLayers />}
          label={t('billing.subscriptions.metric.active')}
          value={formatInteger(counts.active)}
          hint={t('billing.subscriptions.metric.activeHint', { total: formatInteger(counts.all) })}
          loading={subsQ.isLoading}
        />
        <StatCard
          tone="warning" icon={<IconClock />}
          label={t('billing.subscriptions.kpi.pastDue')}
          value={formatInteger(counts.past_due)}
          hint={t('billing.subscriptions.metric.pastDueHint')}
          loading={subsQ.isLoading}
        />
        <StatCard
          tone="info" icon={<IconRepeat />}
          label={t('billing.subscriptions.metric.renewals')}
          value={formatInteger(renewalsThisMonth)}
          hint={t('billing.subscriptions.metric.renewalsHint')}
          loading={subsQ.isLoading}
        />
        <StatCard
          tone="error" icon={<IconAdjustment />}
          label={t('billing.subscriptions.metric.cancelled')}
          value={formatInteger(counts.cancelled)}
          hint={t('billing.subscriptions.metric.cancelledHint')}
          loading={subsQ.isLoading}
        />
      </SimpleGrid>

      <KpiFilterRow
        items={kpiItems}
        value={kpi}
        onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }}
      />

      <BillingToolbar
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('billing.subscriptions.search')}
      >
        <FilterDrawer
          fields={filterFields}
          values={filters}
          onApply={(next) => { setFilters(next); setPage(1); }}
          onClear={clearAllFilters}
        />
      </BillingToolbar>

      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        totalLabel={t('billing.subscriptions.showingCount', { shown: formatInteger(filtered.length), total: formatInteger(all.length) })}
        loading={subsQ.isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={t('billing.subscriptions.empty')}
            description={hasActiveFilters ? t('common.tryClearingFilters') : t('billing.subscriptions.emptyRows')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={
          loadError ? (
            <Alert color="error">
              <Stack gap="sm">
                <Text role="bodySmall">
                  {subsQ.error ? t('billing.subscriptions.loadError') : t('billing.subscriptions.plansLoadError')}
                </Text>
                <Group>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => { void subsQ.refetch(); void plansQ.refetch(); }}
                  >
                    {t('action.retry')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        {view === 'table' && (
          <SubscriptionsTable
            records={paged}
            loading={subsQ.isLoading}
            planLabel={planLabel}
            planPrice={planPrice}
            {...rowNav}
          />
        )}
        {view === 'list' && (
          <SubscriptionsList
            records={paged}
            planLabel={planLabel}
            planPrice={planPrice}
            onSelect={rowNav.onRowClick}
          />
        )}
      </TableShell>

      <RenewalSettingsCard />
      <ExtraUseSettingsCard />
    </Stack>
  );
}
